import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import React, { Suspense, lazy } from "react";

// Componente de loading para lazy load
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B5D]"></div>
  </div>
);

// Pages - Carregamento imediato (críticas)
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import DashboardHome from "./pages/DashboardHome";
import AgendamentosPage from "./pages/AgendamentosPage";

// Pages - Lazy Loading (carregadas sob demanda)
const UsuariosPage = lazy(() => import("./pages/UsuariosPage"));
const PacientesPage = lazy(() => import("./pages/PacientesPage"));
const ProfissionaisPage = lazy(() => import("./pages/ProfissionaisPage"));
const SalasPage = lazy(() => import("./pages/SalasPage"));
const SalasGestaoPage = lazy(() => import("./pages/SalasGestaoPage"));
const MuralPage = lazy(() => import("./pages/MuralPage"));
const ConveniosPage = lazy(() => import("./pages/ConveniosPage"));
const ContratosPage = lazy(() => import("./pages/ContratosPage"));
const ProntuariosPage = lazy(() => import("./pages/ProntuariosPage"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage"));
const RestaurarDesligamentoPage = lazy(() => import("./pages/RestaurarDesligamentoPage"));
const DocumentosProfissionalPage = lazy(() => import("./pages/DocumentosProfissionalPage"));
const DocumentosClinicaPage = lazy(() => import("./pages/DocumentosClinicaPage"));
const PlaceholderPage = lazy(() => import("./pages/PlaceholderPage"));
const ConfiguracoesPage = lazy(() => import("./pages/ConfiguracoesPage"));
const DocumentacaoInternaPage = lazy(() => import("./pages/DocumentacaoInternaPage"));
const TemplatesDocumentosPage = lazy(() => import("./pages/TemplatesDocumentosPage"));
const DocsAdminPage = lazy(() => import("./pages/DocsAdminPage"));
const ConfigHorariosPage = lazy(() => import("./pages/ConfigHorariosPage"));
const ConfiguracoesAgendaPage = lazy(() => import("./pages/ConfiguracoesAgendaPage"));
const DesignPreviewPage = lazy(() => import("./pages/DesignPreviewPage"));
const InteligenciaGerencialPage = lazy(() => import("./pages/InteligenciaGerencialPage"));
const TutoriaisPage = lazy(() => import("./pages/TutoriaisPage"));
const ReposicoesPage = lazy(() => import("./pages/ReposicoesPage"));
const MinhasPendenciasPage = lazy(() => import("./pages/MinhasPendenciasPage"));
const GovernancaAdminPage = lazy(() => import("./pages/GovernancaAdminPage"));
const RelatorioPacientePage = lazy(() => import("./pages/RelatorioPacientePage"));

import ProtectedRoute from "./components/ProtectedRoute";
import AxiosGovernanceInterceptor from "./components/AxiosGovernanceInterceptor";

// Redirect authenticated users away from login
const PublicRoute = ({ children }) => {
  const { session, loading } = useAuth();
  
  if (loading) {
    return null;
  }
  
  if (session) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Design Preview - Public */}
        <Route path="/design-preview" element={<DesignPreviewPage />} />

        {/* Protected Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          
          {/* Admin Routes */}
          <Route 
            path="usuarios" 
            element={
              <ProtectedRoute allowedRoles={['administrador']}>
                <UsuariosPage />
              </ProtectedRoute>
            } 
        />
        <Route 
          path="profissionais" 
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <ProfissionaisPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="financeiro" 
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <PlaceholderPage title="Financeiro" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="relatorios" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'profissional']}>
              <InteligenciaGerencialPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="docs-admin" 
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <DocsAdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="configuracoes" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao', 'profissional']}>
              <ConfiguracoesPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin & Recepção Routes */}
        <Route 
          path="agendamentos" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <AgendamentosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="pacientes" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <PacientesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="checkin" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <PlaceholderPage title="Check-in" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="convenios" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <ConveniosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="salas" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <SalasPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="salas-gestao" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <SalasGestaoPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Mural - Admin, Recepção e Profissional */}
        <Route 
          path="mural" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao', 'profissional']}>
              <MuralPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Documentos da Clínica - Admin e Recepção */}
        <Route 
          path="documentos-clinica" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <DocumentosClinicaPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Gerar Documentos - Recepção (reutiliza a página de profissional) */}
        <Route 
          path="gerar-documentos" 
          element={
            <ProtectedRoute allowedRoles={['recepcao']}>
              <DocumentosProfissionalPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Documentação Interna - Admin e Recepção */}
        <Route 
          path="documentacao-interna" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao']}>
              <DocumentacaoInternaPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Templates de Documentos - Apenas Admin */}
        <Route 
          path="templates-documentos" 
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <TemplatesDocumentosPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Tutoriais - Admin, Recepção e Profissional */}
        <Route 
          path="tutoriais" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao', 'profissional']}>
              <TutoriaisPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Configuração de Horários - Apenas Admin */}
        <Route 
          path="config-horarios" 
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <ConfigHorariosPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Configurações de Agenda (Intervalos) - Admin, Recepção e Profissional */}
        <Route 
          path="config-agenda" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'recepcao', 'profissional']}>
              <ConfiguracoesAgendaPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Contratos - Admin e Profissional (profissional vê apenas o próprio) */}
        <Route 
          path="contratos" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'profissional']}>
              <ContratosPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Auditoria - Apenas Admin */}
        <Route 
          path="auditoria" 
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <AuditLogsPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Restaurar Desligamento - Apenas Admin */}
        <Route 
          path="restaurar-desligamento" 
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <RestaurarDesligamentoPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Reposições - Admin e Profissional */}
        <Route 
          path="reposicoes" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'profissional']}>
              <ReposicoesPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Profissional Routes */}
        <Route 
          path="minha-agenda" 
          element={
            <ProtectedRoute allowedRoles={['profissional']}>
              <AgendamentosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="minhas-pendencias" 
          element={
            <ProtectedRoute allowedRoles={['profissional']}>
              <MinhasPendenciasPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="governanca"
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <GovernancaAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="relatorios/paciente"
          element={
            <ProtectedRoute allowedRoles={['administrador', 'profissional']}>
              <RelatorioPacientePage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="meus-pacientes" 
          element={
            <ProtectedRoute allowedRoles={['profissional']}>
              <PlaceholderPage title="Meus Pacientes" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="documentos" 
          element={
            <ProtectedRoute allowedRoles={['profissional']}>
              <DocumentosProfissionalPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="prontuarios" 
          element={
            <ProtectedRoute allowedRoles={['administrador', 'profissional']}>
              <ProntuariosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="meu-financeiro" 
          element={
            <ProtectedRoute allowedRoles={['profissional']}>
              <PlaceholderPage title="Meu Financeiro" />
            </ProtectedRoute>
          } 
        />
        
        {/* Paciente Routes */}
        <Route 
          path="meus-agendamentos" 
          element={
            <ProtectedRoute allowedRoles={['paciente']}>
              <PlaceholderPage title="Meus Agendamentos" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="historico" 
          element={
            <ProtectedRoute allowedRoles={['paciente']}>
              <PlaceholderPage title="Meu Histórico" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="perfil" 
          element={<PlaceholderPage title="Meu Perfil" />} 
        />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AxiosGovernanceInterceptor />
          <AppRoutes />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
